import {
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  styled,
  Typography,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { getGetMedicationQueryOptions } from '../../../../../api/generated/medication-controller/medication-controller.ts';
import {
  AddressContactDto,
  AddressDto,
  AddressDtoAddressType,
  MedicationDto,
  PatientAddressDtoReferenceType,
  TherapyDto,
  TherapyMedicationDtoPhase,
} from '../../../../../api/generated/model';
import {
  getGetTherapyByPatientQueryKey,
  useCreateTherapyPhase2ByPatient,
} from '../../../../../api/generated/patient-controller/patient-controller.ts';
import { FormActionButtons } from '../../../../../components/form/FormActionButtons.tsx';
import { GermanDateInput } from '../../../../../components/form/GermanDateInput.tsx';
import { InstitutionContactFields } from '../../../../../components/form/InstitutionContactFields.tsx';
import { formatDateForInput, getCurrentDate, getDateAfterInputValue } from '../../../../../utils/date.ts';
import { getStringOrUndefined } from '../../../../../utils/string.ts';
import { SelectMedication } from '../SelectMedication.tsx';

interface FormValues {
  startPhase2: string;
  end: string;
  therapyMedication: MedicationDto[];
  phase2DoctorAddress: AddressDto | null;
  phase2DoctorContact: AddressContactDto | null;
}

export function CreatePhaseTwoDialog({
  open,
  onClose,
  currentTherapy,
}: {
  open: boolean;
  onClose: () => void;
  currentTherapy: TherapyDto;
}) {
  const queryClient = useQueryClient();
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty: isFormDirty },
  } = useForm<FormValues>({
    defaultValues: {
      startPhase2: '',
      end: currentTherapy.end ?? '',
      therapyMedication: [],
      phase2DoctorAddress: null,
      phase2DoctorContact: null,
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const createPhase2Mutation = useCreateTherapyPhase2ByPatient({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getGetTherapyByPatientQueryKey(currentTherapy.patientId),
        });
        void queryClient.resetQueries(getGetMedicationQueryOptions());
        onClose();
      },
      onError: () => {
        toast.error('Beim Speichern der Therapie ist ein Fehler aufgetreten');
      },
    },
  });
  const startPhase2Value = watch('startPhase2');
  const minEndDate =
    getDateAfterInputValue(startPhase2Value) ??
    getDateAfterInputValue(currentTherapy.startPhase1 ? formatDateForInput(currentTherapy.startPhase1) : '');

  const handleOnClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleOnClose}>
      <ModalDialog sx={{ width: 800 }}>
        <ModalClose />
        <Typography level={'h4'}>Phase 2</Typography>
        <Divider />
        <form
          onSubmit={handleSubmit((data) =>
            createPhase2Mutation.mutate({
              id: currentTherapy.patientId,
              data: {
                patientId: currentTherapy.patientId,
                id: currentTherapy.id,
                startPhase2: data.startPhase2,
                end: getStringOrUndefined(data.end),
                finished: currentTherapy.finished,
                therapyResult: currentTherapy.therapyResult,
                resultNote: currentTherapy.resultNote,
                phase2Doctor: {
                  address: data.phase2DoctorAddress!,
                  contact: data.phase2DoctorContact ?? undefined,
                  from: getCurrentDate(),
                  patientId: currentTherapy.patientId,
                  referenceType: PatientAddressDtoReferenceType.THERAPY_PHASE_2,
                },
                therapyMedication: data.therapyMedication.map((medication) => {
                  return { from: data.startPhase2, medication: medication, phase: TherapyMedicationDtoPhase.PHASE_2 };
                }),
              },
            }),
          )}>
          <Stack gap={2}>
            <FormGrid>
              <FormControl error={!!errors.startPhase2}>
                <FormLabel>
                  <Typography>Start Phase 2 *</Typography>
                </FormLabel>
                <GermanDateInput
                  control={control}
                  name="startPhase2"
                  rules={{
                    required: true,
                  }}
                />
                {errors.startPhase2 && <FormHelperText>Startdatum muss ausgefüllt werden</FormHelperText>}
              </FormControl>
              <FormControl error={!!errors.end}>
                <FormLabel>
                  <Typography>Therapie Ende</Typography>
                </FormLabel>
                <GermanDateInput
                  control={control}
                  name="end"
                  rules={{
                    required: false,
                    ...(minEndDate ? { min: { value: minEndDate, message: 'range' } } : {}),
                  }}
                  min={minEndDate}
                />
                {errors.end && <FormHelperText>Therapie Ende muss nach dem Start liegen</FormHelperText>}
              </FormControl>
              <InstitutionContactFields
                formMethods={{ control, watch, setValue }}
                layout="fragment"
                addressField="phase2DoctorAddress"
                contactField="phase2DoctorContact"
                addressLabel="Behandelnder Arzt *"
                contactLabel="Kontakt"
                addressTypes={Object.values(AddressDtoAddressType)}
                canChangeAddressType={false}
                addressRules={{ required: true }}
                addressError={!!errors.phase2DoctorAddress}
                addressHelperText={errors.phase2DoctorAddress ? 'Arzt muss angegeben werden' : undefined}
                clearContactOnAddressChange="always"
                contactClearOptions={false}
                useAllContactsList={true}
              />
              <FormControl sx={{ gridColumn: '1 / span 2' }}>
                <FormLabel>
                  <Typography>Medikamente</Typography>
                </FormLabel>
                <Controller
                  name={'therapyMedication'}
                  control={control}
                  render={({ field: { onChange, value } }) => <SelectMedication value={value} onChange={onChange} />}
                />
              </FormControl>
            </FormGrid>
            <FormActionButtons
              onAbortClick={handleOnClose}
              disableSubmit={!isFormDirty}
              isLoading={createPhase2Mutation.isPending}
            />
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}

const FormGrid = styled('div')`
  display: grid;
  gap: 0.75rem 1rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: start;
`;
